import { createSelector } from 'reselect'
import moment from 'moment'

import { supportedShipsByBrand } from '../../configuration/constants'
import { selectShips } from '../ships/shipsSelectors'
import {
  selectBrandCode,
  selectCurrency,
  selectShipCode,
  selectDate,
} from '../selections/selectionsSelectors'
// import { getDateAsLocal } from '../../utilities'
import royalCaribbeanLogo from '../../assets/images/royal-carribean-logo.png'
import rclVideo from '../../assets/videos/1920x1080_Royal_15sec.mp4'
import celebrityCruisesLogo from '../../assets/images/celebrity-cruises-logo.png'
import celebrityVideo from '../../assets/videos/15 second header V2.mp4'

// eslint-disable-next-line camelcase
const contentByBrand = {
  R: {
    title:
      'HELPING YOUR CLIENTS PLAN MEMORABLE JOURNEYS IS EASIER THAN EVER WITH<br />MY ROYAL CRUISE.',
    description:
      'Select the ship and sail date below to browse the Cruise Planner product catalog for your client’s sailing. You’ll have access to view-only versions of available shore excursions, beverage packages, dining, spa & fitness, and so much more.',
    logoUrl: royalCaribbeanLogo,
    videoUrl: rclVideo,
    copyright: `©${new Date().getFullYear()} Royal Caribbean International.  All Rights Reserved.`,
    footerLinks: [
      {
        text: 'Privacy Policy',
        href: 'https://secure.cruisingpower.com/public/global/privacyPolicy.do',
      },
      {
        text: 'Legal Information',
        href: 'https://secure.cruisingpower.com/public/global/legal.do',
      },
    ],
  },
  C: {
    title:
      'HELPING YOUR CLIENTS PLAN MEMORABLE JOURNEYS IS EASIER THAN EVER WITH<br />MY CELEBRITY CRUISE.',
    description:
      'Select the ship and sail date below to browse the Cruise Planner product catalog for your client’s sailing. You’ll have access to view-only versions of available shore excursions, beverage packages, dining, spa & fitness, and so much more.',
    logoUrl: celebrityCruisesLogo,
    videoUrl: celebrityVideo,
    copyright: `©${new Date().getFullYear()} Celebrity Cruises Inc.  All Rights Reserved.`,
    footerLinks: [
      {
        text: 'Privacy Policy',
        href: 'https://secure.cruisingpower.com/public/global/privacyPolicy.do',
      },
      {
        text: 'Legal Information',
        href: 'https://secure.cruisingpower.com/public/global/legal.do',
      },
    ],
  },
}

const selectStartDate = createSelector(
  selectDate,
  // date value for calendar set could not be string
  date => {
    return date ? moment(date).format('YYYY-MM-DD') : null
  },
)

/*
  NOTE(ebarrett): Original method filtered out currently unsupported ships.
    Instead, we will return all ships and provide messaging if the
    combination of date and ship is unsupported.
*/
const selectValidShips = createSelector(
  selectBrandCode,
  selectShips,
  (brandCode, ships) => {
    const findStartDate = shipCode => {
      const ship = supportedShipsByBrand[brandCode][shipCode]
      if (!ship) return null
      else if (ship.startDate) return ship.startDate
      else return moment().format('YYYY-MM-DD')
    }
    return ships
      .filter(x => x.brand_code === brandCode)
      .sort((a, b) => {
        const A = a.name.toUpperCase()
        const B = b.name.toUpperCase()
        if (A === B) return 0
        else if (A > B) return 1
        else return -1
      })
      .map(ship => ({
        id: ship.ship_code,
        name: ship.name,
        startDate: findStartDate(ship.ship_code) || moment().form,
      }))
  },
)

const selectSubmitUrl = createSelector(
  selectBrandCode,
  selectCurrency,
  selectStartDate,
  selectShipCode,
  (brandCode, currencyCode, date, shipCode) => {
    let submitUrl
    if (brandCode && currencyCode && date && shipCode) {
      const brandDomains = {
        R: 'https://www.royalcaribbean.com',
        C: 'https://www.celebritycruises.com',
      }
      const formattedDateString = moment(date).format('YYYYMMDD')

      // build query url by array
      submitUrl = [
        `${brandDomains[brandCode]}/account/cruise-planner?bookingId=anonymous`,
        `&shipCode=${shipCode}`,
        `&sailDate=${formattedDateString}`,
        `&currencyCode=${currencyCode}`,
      ].join('')
    } else {
      submitUrl = null
    }
    return submitUrl
  },
)

export const selectAppContent = createSelector(
  selectBrandCode,
  selectValidShips,
  selectSubmitUrl,
  (brandCode, ships, submitUrl) => {
    const content = contentByBrand[brandCode]
    return {
      brandCode,
      ...content,
      ships,
      submitUrl,
    }
  },
)

export const selectCurrentShip = createSelector(
  selectValidShips,
  selectShipCode,
  (ships, shipCode) => ships.find(ship => ship.id === shipCode),
)
